import { useState } from "react";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import api from "../../api";

// Offered on the Help page when Help couldn't do what was asked (the answer's
// `request` from /help/ask). Nothing is saved until the person presses Send,
// and they can edit the text first, since people sometimes type patient details.
const INTRO = {
  not_on: "Ask for it to be switched on",
  role: "Ask your clinic admin about it",
  not_available: "Tell us you need this",
  other: "Send it as a request",
};

export default function SendRequestCard({ question, request, isAdmin, compact = false, bare = false, onSent }) {
  const [open, setOpen] = useState(!compact);
  const [text, setText] = useState(question);
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const kind = request?.kind || "other";
  const who = isAdmin ? "the MindConnect team" : "your clinic admin";

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await api.sendHelpRequest({ question: text, details, kind, feature: request?.feature, article_id: request?.article_id });
      setSent(true);
      onSent && onSent();
    } catch (e) {
      setError(e?.response?.data?.error || "Couldn't send it. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 flex gap-2">
        <CheckCircle2 className="w-5 h-5 shrink-0" />
        <span>Sent to {who}. You'll get an alert when they reply, and you can follow it under "My requests" below.</span>
      </div>
    );
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="text-sm text-primary-700 hover:underline">
        Didn't answer your question? Send it as a request
      </button>
    );
  }

  return (
    <div className={bare ? "space-y-3" : "rounded-2xl border border-primary-200 bg-white p-5 space-y-3 shadow-sm"}>
      <div className={bare ? "hidden" : ""}>
        <p className="font-semibold text-secondary-900">{INTRO[kind] || INTRO.other}</p>
        <p className="text-sm text-secondary-600 mt-0.5">
          {isAdmin
            ? "This goes to the MindConnect team, who'll get back to you."
            : "This goes to your clinic admin, who can talk to you about it and pass it on to MindConnect."}
        </p>
      </div>
      <label className="block normal-case font-sans tracking-normal text-sm text-secondary-700">
        What were you trying to do?
        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={500} rows={2}
          className="mt-1 w-full rounded-xl border border-secondary-300 px-3 py-2 text-sm text-secondary-900 focus:ring-2 focus:ring-primary-500" />
      </label>
      <label className="block normal-case font-sans tracking-normal text-sm text-secondary-700">
        Anything else that would help? <span className="text-secondary-400">(optional)</span>
        <textarea value={details} onChange={(e) => setDetails(e.target.value)} maxLength={1000} rows={2}
          placeholder="e.g. how often you need it, or what you do today instead"
          className="mt-1 w-full rounded-xl border border-secondary-300 px-3 py-2 text-sm text-secondary-900 focus:ring-2 focus:ring-primary-500" />
      </label>
      <p className="text-xs text-secondary-500">Please don't include patient names or details.</p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={send} disabled={sending || !text.trim()}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send request
        </button>
        {compact && (
          <button type="button" onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-secondary-600 hover:text-secondary-900">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
